import { assign, map, find } from 'lodash';
import { AercoMetric, MetricJSON } from './Metrics';
import {
  WalkScoreJSON,
  UnitTypeJSON,
  SaleInformationJSON,
  FloorPlanJSON,
  UnitTypeMetricJSON,
  FloorPlanMetricJSON,
  MarkerType,
  UnitTypeMetric,
  FloorPlanMetric,
  AddressJSON,
  BedRoomTypeJSON,
  StudentAercoMetric,
  StudentUnitTypeMetric
} from '~/core/model';
import { AppConstants } from '~/shared/utils/app-constants';

export interface PropertyJSON extends AddressJSON {
  propertyId: number;
  propertyName: string;
  propertyType: string;
  position: string;
  market: string;
  submarket: string;
  unitCount: number;
  stories: string | number;
  stable: string;
  class: string;
  type: string;
  distance: number;
  latitude: string;
  longitude: string;
  walkScore: WalkScoreJSON;
}

export interface PropertyAERCOJSON extends PropertyJSON {
  metrics: PropertyMetricJSON[];
  unitTypes: UnitTypeMetricJSON[];
  floorplans: FloorPlanMetricJSON[];
  bedRoomType: BedRoomTypeJSON[];
}

export interface PropertyInfoJSON extends PropertyDetailJSON {
  salePrice: number;
  capRate: number;
  ppu: number;
  salesDate: string;
  averageRpsf: number;
}

export interface PropertyMetricJSON {
  propertyId: number;
  metrics: MetricJSON;
}

export interface PropertyMetricCollectionJSON {
  propertyId: number;
  metrics: MetricJSON[];
}

export interface PlaceJSON {
  name: string;
  vicinity: string;
}

export interface PlacesDetailJSON {
  elementarySchool: PlaceJSON[];
  middleSchool: PlaceJSON[];
  grocery: PlaceJSON[];
  transportation: PlaceJSON[];
}

export interface PropertyInfo {
  propertyId: number;
  propertyName: string;
}

export class Places implements PlacesDetailJSON {
  elementarySchool: PlaceJSON[];
  grocery: PlaceJSON[];
  transportation: PlaceJSON[];
  middleSchool: PlaceJSON[];

  constructor(json: PlacesDetailJSON) {
    if (!json) return this;
    for (var key in json) {
      this.setPlaces(key, json[key], this);
    }
  }

  setPlaces(key: string, places, obj: Places) {
    if (places && places.length > 0) {
      obj[key] = places.map(place => {
        return {
          name: place && place.name,
          vicinity: place && place.vicinity
        }
      })
    }
  }

}

export interface PropertyDetailJSON extends PropertyJSON {
  averageSquareFootage?: number;
  totalSquareFootage: string | number;
  yearBuilt: string;
  imageHero: string;
  heroSource: string;
  overview: string;
  amenities: string[];
  unitTypes: UnitTypeJSON[];
  floorplans: FloorPlanJSON[];
  saleInformation: SaleInformationJSON[];
  places: PlacesDetailJSON;
  metricData?: MetricJSON[];
}

export class Property implements PropertyJSON {
  id: number;
  propertyId: number;
  propertyName: string;
  propertyType: string;
  propertyUrlSlug: string;
  position: string;
  market: string;
  submarket: string;
  unitCount: number;
  stories: string | number;
  stable: string;
  class: string;
  type: string;
  distance: number;
  latitude: string;
  longitude: string;
  walkScore: WalkScoreJSON;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  fullAddress: string;

  static sortPropertys<T extends Property>(l: T, r: T, descending = false): number {
    return (+l.position - +r.position) * (descending ? -1 : 1);
  }

  constructor(json: PropertyJSON) {
    if (!json) return this;
    assign(this, json);
    this.id = json.propertyId;

    if (!!this.zipCode && typeof this.zipCode === 'string') this.zipCode = this.zipCode.substring(0, 5);
    this.fullAddress = this._fullAddress();
  }

  get isSubject() {
    return this.propertyType && this.propertyType.length && this.propertyType === 'SUBJECT';
  }

  static isSubject<T extends PropertyJSON>(p: T): boolean {
    return p.propertyType && p.propertyType.length && p.propertyType === 'SUBJECT';
  }

  static isComparable<T extends PropertyJSON>(p: T): boolean {
    return p.propertyType && p.propertyType.length && p.propertyType === 'COMPARISON';
  }

  static isSalesComparable<T extends PropertyJSON>(p: T): boolean {
    return p.propertyType && p.propertyType.length && p.propertyType === 'SALES_COMPARISON';
  }

  static sortProperties<T extends Property>(l: T, r: T, descending: boolean = false): number {
    if (Property.isSubject(l)) return -1;
    if (Property.isSubject(r)) return 1;
    return (+l.position - (+r.position)) * (descending ? -1 : 1);
  }

  static updatePosition<T extends PropertyJSON>(property: T, type: PropertyType | string, position: string | number): T {
    if (!property) return property;

    property.propertyType = typeof type === 'string' ? type : type.key;
    property.position = position.toString();
    return property;
  }

  private _fullAddress(): string {
    let str = '';

    if (!!this.address) {
      str += this.address;
    }

    if (!!this.city && !!this.address) {
      str += ', ' + this.city;
    } else if (!!this.city) {
      str += this.city;
    }

    if (!!this.city && !!this.state) {
      str += ', ' + this.state;
    } else if (!!this.state) {
      str += this.state;
    }
    if (!!this.state && !!this.zipCode) {
      str += ' ' + this.zipCode;
    } else if (!!this.zipCode) {
      str += this.zipCode;
    }

    return str;
  }

  updatePosition(type: MarkerType | string, position: string) {
    const markerType = typeof type === 'string' ? type : type.key;
    if (this.type === markerType && this.position === position) return;
    this.type = markerType;
    this.position = position;
  }
}

export class PropertyDetail
  extends Property
  implements PropertyDetailJSON, PropertyJSON {
  id: number;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  distance: number;
  fullAddress: string;
  market: string;
  submarket: string;
  unitCount: number;
  averageSquareFootage?: number;
  totalSquareFootage: number;
  yearBuilt: string;
  stories: number;
  stable: string;
  class: string;
  type: string;
  latitude: string;
  longitude: string;
  imageHero: string;
  heroSource: string;
  overview: string;
  walkScore: WalkScoreJSON;
  propertyUrlSlug: string;
  amenities: string[];
  unitTypes: UnitTypeJSON[];
  floorplans: FloorPlanJSON[];
  saleInformation: SaleInformationJSON[];
  places: PlacesDetailJSON;
  order?: number;

  constructor(json: PropertyDetailJSON) {
    super(json);
    this.id = +this.propertyId;
    if (!json) return this;
    assign(this, json);
    if (this.totalSquareFootage) this.totalSquareFootage = +this.totalSquareFootage;
    if (this.stories) this.stories = +this.stories;
    if (this.places) this.places = new Places(this.places);
  }
}

export class PropertyMetric extends PropertyDetail implements PropertyJSON {
  metrics: AercoMetric | StudentAercoMetric;
  unitTypes: UnitTypeMetric[] | StudentUnitTypeMetric[];
  floorplans: FloorPlanMetric[];

  constructor(details: PropertyDetailJSON, aerco: PropertyAERCOJSON) {
    super(details);

    if(details.type === AppConstants.TYPES.STUDENT_HOUSING) {
      this.metrics = aerco.metrics ? StudentAercoMetric.fromJSON(map(aerco.metrics, m => m.metrics)) : null;
      this.unitTypes = map(aerco && aerco.bedRoomType, bedRoomType => {
        return new StudentUnitTypeMetric({
          avgsqftperbed: bedRoomType.avgsqftperbed,
          bedroomtypedisplay: bedRoomType.bedroomtypedisplay
        }, bedRoomType);
      });
    } else {
      if (aerco) {
        this.metrics = aerco.metrics ? AercoMetric.fromJSON(map(aerco.metrics, m => m.metrics)) : null;      
        
        this.unitTypes = map(details && details.unitTypes,
          ut => new UnitTypeMetric(ut, find(aerco && aerco.unitTypes, m => m.unittype === ut.unitType)));
  
        this.floorplans = map(details && details.floorplans,
          fp => new FloorPlanMetric(fp, find(aerco && aerco.floorplans, m => m.floorplan === fp.floorplan)));
      } else {
        this.unitTypes = map(details && details.unitTypes, ut => new UnitTypeMetric(ut, null));
      }
    }
  }
}

export interface PropertyType {
  key: string;
  name: string;
  label: string;
  icon?: string;
}

class PropertyTypes {
  SUBJECT: PropertyType = {
    key: 'SUBJECT',
    name: 'subject',
    label: 'Subject',
    icon: '/assets/images/subjmarker.png'
  };

  RENT_COMP: PropertyType = {
    key: 'COMPARISON',
    name: 'comparison',
    label: 'Comps',
    icon: '/assets/images/rent-comps-markers'
  };

  SALES_COMP: PropertyType = {
    key: 'SALES_COMPARISON',
    name: 'comparison',
    label: 'Comps',
    icon: '/assets/images/sales-comps-markers'
  };

  CONSTRUCTION: PropertyType = {
    key: 'CONSTRUCTION',
    name: 'construction',
    label: 'Construction Pipeline'
  };

  NONE: PropertyType = {
    key: 'NONE',
    name: 'none',
    label: 'None',
    icon: '/assets/images/sales-comps-markers'
  };

  isSubject<T extends PropertyJSON>(t: T): boolean {
    return !!t && t.propertyType === this.SUBJECT.key;
  }

  isRentComp<T extends PropertyJSON>(t: T): boolean {
    return !!t && t.propertyType === this.RENT_COMP.key;
  }

  isSalesComp<T extends PropertyJSON>(t: T): boolean {
    return !!t && t.propertyType === this.SALES_COMP.key;
  }

  sortByPosition<T extends PropertyJSON>(l: T, r: T): number {
    return (+l.position) - (+r.position);
  }
}

export const PROPERTY_TYPE = new PropertyTypes();



// WEBPACK FOOTER //
// ./src/app/core/model/Property.ts